/**
 * Electrical resistance formatting utilities
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { FieldValue, FormattedValue, ResistanceFormat } from "./types";

/**
 * Format as milliohms
 */
export const toMilliohms = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  const mOhms = value * 1000;
  return `${mOhms.toFixed(decimals)} mΩ`;
};

/**
 * Format as ohms
 */
export const toOhms = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${value.toFixed(decimals)} Ω`;
};

/**
 * Format as kilohms
 */
export const toKilohms = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1000).toFixed(decimals)} kΩ`;
};

/**
 * Format as megohms
 */
export const toMegohms = (value: number | null | undefined, decimals: number = 2): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1_000_000).toFixed(decimals)} MΩ`;
};

/**
 * Auto-scale resistance to the most appropriate unit
 */
export const autoScaleResistance = (value: number | null | undefined, decimals?: number): string | null => {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  if (absValue >= 1_000_000) {
    return toMegohms(value, decimals ?? 2);
  } else if (absValue >= 1000) {
    return toKilohms(value, decimals ?? 1);
  } else if (absValue < 1) {
    return toMilliohms(value, decimals ?? 1);
  } else {
    return toOhms(value, decimals ?? 1);
  }
};

/**
 * Format resistance according to ResistanceFormat specification
 */
export const formatResistance = (value: FieldValue, format: ResistanceFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const decimals = format.decimals;

  // If a specific unit is provided, use it
  if (format.unit) {
    switch (format.unit) {
      case "MΩ":
        return toMegohms(numValue, decimals ?? 2);
      case "kΩ":
        return toKilohms(numValue, decimals ?? 1);
      case "mΩ":
        return toMilliohms(numValue, decimals ?? 1);
      case "Ω":
        return toOhms(numValue, decimals ?? 1);
    }
  }

  // Default to auto-scaling unless explicitly disabled
  if (format.autoScale !== false) {
    return autoScaleResistance(numValue, decimals);
  }

  // If auto-scale is explicitly false and no unit specified, use base ohms
  return toOhms(numValue, decimals ?? 1);
};

// Register the formatter
// biome-ignore lint/suspicious/noExplicitAny: Required for FormatRegistry type compatibility
FormatRegistry.register("resistance", formatResistance as any);

// Register legacy shortcuts
FormatRegistry.register("ohms", (value) => {
  if (typeof value === "number") return toOhms(value);
  return null;
});

FormatRegistry.register("milliohms", (value) => {
  if (typeof value === "number") return toMilliohms(value);
  return null;
});
